import {
  breakpointSizes,
  getBreakpointDown,
  getFontCss,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import React from 'react'
import { useDispatch } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import { BudgetScreen } from 'src/app/CreateBudget/CreateBudget'
import { InvitePeopleScreen } from 'src/app/InvitePeople/InvitePeople'
import { UpdateVirtualCardScreen } from 'src/app/UpdateVirtualCardForm/UpdateVirtualCardForm'
import CloseButton, {
  CloseButtonDesigns,
} from 'src/components/CloseButton/CloseButton'
import HoverableLink from 'src/components/HoverableLink/HoverableLink'
import { closeModal } from 'src/navigation/Lnk'
import { css } from 'styled-components'

// TODO: refactor this
interface ActionRightFlyoutContentSpecificElementProps {
  subtitle?: React.ReactElement
  imageSrc?: string
  imageComponent?: React.ReactElement
  rotateImage?: boolean
  changeLink?: ContainerAriaSourceProps | ContainerAriaLabelProps
}

interface ChangeLinkType {
  changeMessage: React.ReactElement
  onChangeClick: () => void
}

interface ContainerAriaSourceProps extends ChangeLinkType {
  ariaLabelSource: string
  ariaLabel?: undefined
}

interface ContainerAriaLabelProps extends ChangeLinkType {
  ariaLabel: string
  ariaLabelSource?: undefined
}

interface ActionRightFlyoutContentPermissionsProps {
  canUpdateVirtualCard?: boolean
  isCardPending?: boolean
  isBudget?: boolean
}

interface ActionRightFlyoutContentProps {
  testId?: string
  className?: string
  title: React.ReactElement
  pickerSubtitle?: React.ReactElement
  specificElements?: ActionRightFlyoutContentSpecificElementProps
  cardChangeNotAllowed?: boolean
  permissions?: ActionRightFlyoutContentPermissionsProps
  screen?: InvitePeopleScreen | BudgetScreen | UpdateVirtualCardScreen
  onClose?: () => void
}

const ActionRightFlyoutContent: React.FC<
  React.PropsWithChildren<ActionRightFlyoutContentProps>
> = (props) => {
  const dispatch = useDispatch()
  const { analytics } = useAnalyticsContext()

  const handleNavigateAwayAnalytics = (): void => {
    props.permissions?.isBudget &&
      analytics?.track('create budget flyout - navigate away')
    props.screen === InvitePeopleScreen.Contact &&
      analytics?.track(
        'invite people contacts flyout - navigate away - button press',
        {
          screen: '/invite/people',
          interaction: 'button press',
          intent: 'navigate away',
        },
      )
    props.permissions?.canUpdateVirtualCard === false &&
      analytics?.track(
        'request virtual card update flyout - navigate away - button press',
        {
          screen: '/virtualcards',
          interaction: 'button press',
          intent: 'navigate away',
        },
      )
  }

  const handleChangeTypeAnalytics = (): void => {
    props.permissions?.isBudget &&
      analytics?.track(
        'create budget flyout - change budget type - text link press',
        {
          screen: '/createABudget',
          interaction: 'text link press',
          intent: 'change budget type',
        },
      )
    props.permissions?.canUpdateVirtualCard &&
      props.permissions?.isCardPending &&
      props.screen === UpdateVirtualCardScreen.Pick &&
      analytics?.track(
        'approve virtual card request flyout - change card type - text link press',
        {
          screen: '/virtualcards',
          interaction: 'text link press',
          intent: 'change card type',
        },
      )
    props.permissions?.canUpdateVirtualCard === false &&
      props.screen === UpdateVirtualCardScreen.Pick &&
      analytics?.track(
        'request virtual card update flyout - change card type - text link press',
        {
          screen: '/virtualcards',
          interaction: 'text link press',
          intent: 'change card type',
        },
      )
  }

  return (
    <div
      className={props.className}
      data-testid={props.testId}
      css={css`
        display: flex;
        flex-direction: column;
      `}
    >
      <div
        css={css`
          position: relative;
          padding: 30px 48px;
          background-color: ${styleConstants.colors.white};
          display: flex;
          flex-direction: column;
          border-bottom: 1px solid ${styleConstants.colors.gray_medium};
          overflow: hidden;
          flex-shrink: 0;

          ${getBreakpointDown(breakpointSizes.XSM)(css`
            padding: 20px 30px;
          `)}
        `}
      >
        <CloseButton
          css={css`
            margin-bottom: 20px;

            ${getBreakpointDown(breakpointSizes.XSM)(css`
              align-self: flex-end;
            `)}
          `}
          design={CloseButtonDesigns.DarkOnLight}
          onClick={(): void => {
            dispatch(closeModal())
            handleNavigateAwayAnalytics()
            if (props.onClose) {
              props.onClose()
            }
          }}
        />
        {props.specificElements?.imageSrc && (
          <img
            css={css`
              transform: scale(1.5);
              position: absolute;
              top: -36px;
              right: -36px;

              ${!!props.specificElements.rotateImage &&
              css`
                width: 120px;
                transform: rotate(-30deg);
              `}

              ${getBreakpointDown(breakpointSizes.XSM)(css`
                display: none;
              `)}
            `}
            src={props.specificElements.imageSrc}
            alt={''}
          />
        )}
        {props.specificElements?.imageComponent !== undefined &&
          props.specificElements?.imageComponent}
        <span
          css={css`
            ${getFontCss(
              32,
              themeConstants.colors.font_primary,
              styleConstants.fontFamilies.medium,
            )};
            margin-bottom: 10px;

            ${getBreakpointDown(breakpointSizes.XSM)(css`
              ${getFontCss(
                20,
                themeConstants.colors.font_primary,
                styleConstants.fontFamilies.medium,
              )};
            `)}
          `}
        >
          {props.title}
        </span>
        {props.pickerSubtitle && (
          <span
            css={css`
              ${getFontCss(
                16,
                themeConstants.colors.font_primary,
                styleConstants.fontFamilies.regular,
              )};
            `}
          >
            {props.pickerSubtitle}
          </span>
        )}
        {props.specificElements && (
          <div
            css={css`
              display: flex;
              justify-content: space-between;
              align-items: flex-end;
            `}
          >
            <span
              css={css`
                ${getFontCss(
                  32,
                  styleConstants.colors.gray_dark,
                  styleConstants.fontFamilies.medium,
                )};

                ${getBreakpointDown(breakpointSizes.XSM)(css`
                  ${getFontCss(
                    20,
                    styleConstants.colors.gray_dark,
                    styleConstants.fontFamilies.medium,
                  )};
                `)}
              `}
            >
              {props.specificElements.subtitle}
            </span>
            {props.specificElements.changeLink !== undefined &&
              !props?.cardChangeNotAllowed && (
                <span
                  onClick={(): void => {
                    handleChangeTypeAnalytics()
                  }}
                  onKeyPress={(): null => {
                    return null
                  }}
                  role="button"
                  tabIndex={0}
                >
                  <HoverableLink
                    onClick={props.specificElements.changeLink.onChangeClick}
                    {...(props.specificElements.changeLink.ariaLabelSource !==
                    undefined
                      ? {
                          ariaLabelSource:
                            props.specificElements.changeLink.ariaLabelSource,
                        }
                      : {
                          ariaLabel:
                            props.specificElements.changeLink.ariaLabel,
                        })}
                  >
                    {props.specificElements.changeLink.changeMessage}
                  </HoverableLink>
                </span>
              )}
          </div>
        )}
      </div>
      <div
        css={css`
          flex-grow: 2;
          background-color: ${styleConstants.colors.gray_light};
          display: flex;
          flex-direction: column;
          padding: 30px 48px;

          ${getBreakpointDown(breakpointSizes.XSM)(css`
            padding: 20px 20px 100px;
          `)}
        `}
      >
        {props.children}
      </div>
    </div>
  )
}

export default ActionRightFlyoutContent
